import type { ApolloClient, ObservableQuery } from "@apollo/client";

/**
 * App-wide signal used to refresh the data on the page the user is currently
 * viewing without a navigation. This is needed because React Router treats a
 * click on a link to the current URL as a no-op, so a notification/alert that
 * points to the page you are already on would otherwise do nothing — leaving
 * stale data on screen after a change was made elsewhere.
 */
export const ROUTE_REFRESH_EXCLUDED_OPERATIONS = [
  "Me",
  "EnabledFeatureFlags",
  "Accounts",
  "Account",
] as const;

export function shouldSkipRouteRefreshQuery(
  queryName: string | undefined
): boolean {
  return (
    queryName != null &&
    (ROUTE_REFRESH_EXCLUDED_OPERATIONS as readonly string[]).includes(queryName)
  );
}

/** Refetches active page queries, skipping always-mounted app-shell operations. */
export function refetchActiveRoutePageQueries(client: ApolloClient): void {
  void client.refetchQueries({
    include: "active",
    onQueryUpdated(observableQuery: ObservableQuery) {
      if (shouldSkipRouteRefreshQuery(observableQuery.queryName)) {
        return false;
      }
      return observableQuery.refetch();
    },
  });
}

export const isCurrentPath = (to: string, currentPathname: string): boolean => {
  if (!to) return false;
  const targetPathname = to.split(/[?#]/)[0];
  return targetPathname === currentPathname;
};
